/**
 * Utility para cargar dinámicamente las imágenes de artistas desde la estructura de carpetas
 * Estructura: fotos/Dia-Numero/NombreArtista/imagen.*
 */

// Cargar todas las imágenes de la carpeta fotos recursivamente
// El parámetro 'true' hace que sea recursivo
// La regex busca cualquier imagen (jpg, jpeg, png, JPG, etc.)
const images = require.context('./fotos', true, /\.(jpe?g|png|JPE?G|PNG)$/);

/**
 * Mapeo del orden de artistas por día (orden de presentación en la noche)
 * El número indica la posición: 1 = primero, 2 = segundo, etc.
 */
const ordenArtistasPorDia = {
    'Viernes-6': [
        'Luciano Pereyra',
        'Soledad',
        'Jorge Rojas',
        'Chaqueño Palavecino',
        'Los Tekis'
    ],
    'Sábado-7': [
        'Luck Ra',
        'Emanero',
        'La Joaqui',
        'Q´Lokura'
    ],
    'Domingo-8': [
        'Fito Paez',
        'Abel',
        'Fabricio Rodriguez',
        'Emma Roach'
    ],
    'Lunes-9': [
        'Desakata2',           // Mapeado a "Desakatados"
        'Euge Quevedo y LBC',  // Mapeado a "LBC y Euge Quevedo"
        'Ulises Bueno',        // Mapeado a "Ulises"
        'Pepa Brizuela',       // Mapeado a "La Pepa Brizuela"
        'Valentina Marquez',
        'Simón Aguirre',
        'Vacomoloko'
    ],
    'Martes-10': [
        'MORAT',
        'Coti',
        'Juli Cardozo'
    ]
};

/**
 * Mapeo de nombres de carpetas a nombres de visualización
 */
const nombresVisualizacion = {
    'Desakata2': 'Desakatados',
    'Euge Quevedo y LBC': 'LBC y Euge Quevedo',
    'Ulises Bueno': 'Ulises',
    'Pepa Brizuela': 'La Pepa Brizuela',
    'MORAT': 'Morat',
    'Abel': 'Abel Pintos',
    'Fito Paez': 'Fito Páez'
};

/**
 * Procesa las rutas de las imágenes y organiza por días y artistas
 * @returns {Array} Array de objetos con estructura: { dia: "Viernes 6", artistas: [...] }
 */
export function loadArtistasData() {
    const diasMap = {};

    // Iterar sobre todas las imágenes encontradas
    images.keys().forEach((imagePath) => {
        // Ejemplo de imagePath: "./Viernes-6/Soledad/index.JPG"
        const parts = imagePath.split('/');
        
        // Verificar que tenga la estructura correcta: ./DiaFolder/ArtistaFolder/imagen.ext
        if (parts.length >= 4) {
            const diaFolder = parts[1]; // "Viernes-6"
            const artistaFolder = parts[2]; // "Soledad"
            const imageName = parts[parts.length - 1]; // "index.JPG"
            
            // Parsear el nombre del día: "Viernes-6" -> "Viernes 6"
            const [diaNombre, diaNumero] = diaFolder.split('-');
            const diaDisplay = `${diaNombre} ${diaNumero}`;
            
            // Inicializar el día si no existe
            if (!diasMap[diaDisplay]) {
                diasMap[diaDisplay] = {
                    dia: diaDisplay,
                    diaKey: diaFolder,
                    artistas: {}
                };
            }
            
            // Inicializar el artista si no existe
            if (!diasMap[diaDisplay].artistas[artistaFolder]) {
                diasMap[diaDisplay].artistas[artistaFolder] = {
                    nombre: artistaFolder,
                    imagenes: []
                };
            }
            
            // Agregar la imagen al artista
            diasMap[diaDisplay].artistas[artistaFolder].imagenes.push({
                path: imagePath,
                url: images(imagePath),
                fileName: imageName
            });
        }
    });

    // Convertir el map a array y procesar
    const diasArray = Object.values(diasMap).map(dia => {
        // Obtener el orden definido para este día
        const ordenDia = ordenArtistasPorDia[dia.diaKey] || [];
        
        // Convertir artistas de objeto a array, manteniendo el nombre de carpeta original
        const artistasConCarpeta = Object.entries(dia.artistas).map(([nombreCarpeta, artista]) => {
            // Ordenar imágenes: priorizar "index.*" primero
            const imagenesOrdenadas = artista.imagenes.sort((a, b) => {
                const aIsIndex = a.fileName.toLowerCase().startsWith('index');
                const bIsIndex = b.fileName.toLowerCase().startsWith('index');
                
                if (aIsIndex && !bIsIndex) return -1;
                if (!aIsIndex && bIsIndex) return 1;
                return 0;
            });

            // Obtener nombre de visualización (si existe mapeo, usar ese, sino el original)
            const nombreVisualizacion = nombresVisualizacion[nombreCarpeta] || nombreCarpeta;

            return {
                nombreCarpeta, // Guardar el nombre original de la carpeta para ordenar
                id: nombreVisualizacion.replace(/\s+/g, '-').toLowerCase(),
                nombre: nombreVisualizacion,
                // Imagen principal es la primera (index si existe, o la primera disponible)
                imagenPrincipal: imagenesOrdenadas[0]?.url || '',
                // Todas las imágenes disponibles
                imagenes: imagenesOrdenadas.map(img => img.url)
            };
        });

        // Ordenar artistas según el orden definido para este día
        if (ordenDia.length > 0) {
            artistasConCarpeta.sort((a, b) => {
                const aIndex = ordenDia.indexOf(a.nombreCarpeta);
                const bIndex = ordenDia.indexOf(b.nombreCarpeta);

                // Si no están en el orden definido, mantener el orden original
                if (aIndex === -1 && bIndex === -1) return 0;
                if (aIndex === -1) return 1;
                if (bIndex === -1) return -1;

                return aIndex - bIndex;
            });
        }

        // Remover el nombreCarpeta antes de retornar (ya no es necesario)
        const artistasArray = artistasConCarpeta.map(({ nombreCarpeta, ...artista }) => artista);

        return {
            dia: dia.dia,
            diaKey: dia.diaKey,
            artistas: artistasArray
        };
    });

    // Ordenar días: Viernes, Sábado, Domingo, Lunes, Martes
    const ordenDias = { 'Viernes': 1, 'Sábado': 2, 'Domingo': 3, 'Lunes': 4, 'Martes': 5 };
    
    diasArray.sort((a, b) => {
        const aNombre = a.dia.split(' ')[0];
        const bNombre = b.dia.split(' ')[0];
        
        return (ordenDias[aNombre] || 999) - (ordenDias[bNombre] || 999);
    });

    return diasArray;
}

// Exportar también los datos precargados para usar directamente
export const artistasData = loadArtistasData();

